import { request } from '../request';

/**
 * 获取我的发行项目列表
 * GET /my/projects
 *
 * @param params 查询参数 (keyword: 关键字, status: 项目状态, updated_start: 更新时间开始, updated_end: 更新时间结束, page: 页码, page_size: 每页数量)
 * @returns 项目分页列表响应数据
 */
export function fetchMyProjects(params?: Api.Project.QueryProjectsParams) {
  return request<Api.Project.ProjectPageResponse>({
    url: '/my/projects',
    method: 'GET',
    params
  });
}

/**
 * 获取我的发行项目详情
 * GET /my/projects/{id}
 *
 * @param id 发行项目 ID
 * @returns 项目详细信息（包含渠道配置、合同、收益概况、来源申请等）
 */
export function fetchProjectDetail(id: number | string) {
  return request<Api.Project.ProjectDetailResponse>({
    url: `/my/projects/${id}`,
    method: 'GET'
  });
}

/**
 * 获取我的项目渠道
 * GET /my/projects/{id}/channels
 *
 * @param id 发行项目 ID
 * @returns 项目渠道配置列表
 */
export function fetchProjectChannels(id: number | string) {
  return request<Api.Project.ProjectChannelResponse[]>({
    url: `/my/projects/${id}/channels`,
    method: 'GET'
  });
}

/**
 * 获取我的项目合同
 * GET /my/projects/{id}/contract
 *
 * @param id 发行项目 ID
 * @returns 项目关联的合同响应数据
 */
export function fetchProjectContract(id: number | string) {
  return request<Api.Project.ContractResponse>({
    url: `/my/projects/${id}/contract`,
    method: 'GET'
  });
}

/**
 * 下载项目合同
 * POST /my/projects/{id}/contract/download
 *
 * @param id 发行项目 ID
 * @returns 合同信息及下载文件资源
 */
export function fetchDownloadProjectContract(id: number | string) {
  return request<Api.Project.ContractResponse>({
    url: `/my/projects/${id}/contract/download`,
    method: 'POST'
  });
}

/**
 * 上传内容方签署合同
 * POST /my/projects/{id}/contract/upload-owner-signed
 *
 * @param id 发行项目 ID
 * @param data 请求体 (owner_signed_asset_id: 内容方签署的资源 ID)
 * @returns 更新后的合同响应数据
 */
export function fetchUploadOwnerSignedContract(
  id: number | string,
  data: Api.Project.UploadOwnerSignedRequest
) {
  return request<Api.Project.ContractResponse>({
    url: `/my/projects/${id}/contract/upload-owner-signed`,
    method: 'POST',
    data
  });
}

/**
 * 查询项目剧集及当前审核状态
 * GET /my/projects/{id}/episodes
 *
 * @param id 发行项目 ID
 * @returns 剧集列表及审核状态
 */
export function fetchProjectEpisodes(id: number | string) {
  return request<Api.Project.ProjectEpisodeResponse[]>({
    url: `/my/projects/${id}/episodes`,
    method: 'GET'
  });
}

/**
 * 替换项目剧集文件
 * PUT /my/projects/{id}/episodes/{epNo}/replace
 *
 * @param id 发行项目 ID
 * @param epNo 单集集数/编号
 * @param data 请求体 (upload_asset_id: 新视频上传资源 ID)
 * @returns 替换后的剧集响应数据列表
 */
export function fetchReplaceProjectEpisode(
  id: number | string,
  epNo: number | string,
  data?: Api.Project.ReplaceEpisodeRequest
) {
  return request<Api.Project.ProjectEpisodeResponse[]>({
    url: `/my/projects/${id}/episodes/${epNo}/replace`,
    method: 'PUT',
    data
  });
}

/**
 * 查询项目剧集文件版本
 * GET /my/projects/{id}/episodes/{epNo}/versions
 *
 * @param id 发行项目 ID
 * @param epNo 单集集数/编号
 * @returns 剧集历史文件版本列表
 */
export function fetchProjectEpisodeVersions(
  id: number | string,
  epNo: number | string
) {
  return request<Api.Project.EpisodeFileVersionResponse[]>({
    url: `/my/projects/${id}/episodes/${epNo}/versions`,
    method: 'GET'
  });
}

/**
 * 新增项目后续剧集（批量添加）
 * POST /my/projects/{id}/episodes/batch
 *
 * @param id 发行项目 ID
 * @param data 请求体 (episodes: 剧集参数数组)
 * @returns 新增的剧集响应列表
 */
export function fetchBatchAddProjectEpisodes(
  id: number | string,
  data?: Api.Project.BatchAddEpisodesRequest
) {
  return request<Api.Project.ProjectEpisodeResponse[]>({
    url: `/my/projects/${id}/episodes/batch`,
    method: 'POST',
    data
  });
}

/**
 * 查询我的项目剧集交付清单
 * GET /my/projects/{id}/episode-deliveries
 *
 * @param id 发行项目 ID
 * @param params 查询参数 (source: 交付来源, page: 页码, page_size: 每页数量)
 * @returns 剧集交付清单分页列表
 */
export function fetchProjectEpisodeDeliveries(
  id: number | string,
  params?: Api.Project.QueryDeliveriesParams
) {
  return request<Api.Project.EpisodeDeliveryPageResponse>({
    url: `/my/projects/${id}/episode-deliveries`,
    method: 'GET',
    params
  });
}

/**
 * 恢复项目剧集历史文件版本
 * POST /my/projects/{id}/episodes/{epNo}/versions/{versionId}/restore
 *
 * @param id 发行项目 ID
 * @param epNo 单集集数/编号
 * @param versionId 剧集文件版本 ID
 * @returns 恢复后的剧集列表响应数据
 */
export function fetchRestoreProjectEpisodeVersion(
  id: number | string,
  epNo: number | string,
  versionId: number | string
) {
  return request<Api.Project.ProjectEpisodeResponse[]>({
    url: `/my/projects/${id}/episodes/${epNo}/versions/${versionId}/restore`,
    method: 'POST'
  });
}

/**
 * 获取我的项目剧集交付摘要
 * GET /my/projects/{id}/episodes/summary
 *
 * @param id 发行项目 ID
 * @returns 项目剧集交付及审核统计摘要
 */
export function fetchProjectEpisodeSummary(id: number | string) {
  return request<Api.Project.EpisodeSummaryResponse>({
    url: `/my/projects/${id}/episodes/summary`,
    method: 'GET'
  });
}

/**
 * 查询我的项目来源申请资料
 * GET /my/projects/{id}/source-application-assets
 *
 * @param id 发行项目 ID
 * @returns 项目来源申请资料分组列表
 */
export function fetchProjectSourceApplicationAssets(id: number | string) {
  return request<Api.Project.SourceAssetGroupResponse[]>({
    url: `/my/projects/${id}/source-application-assets`,
    method: 'GET'
  });
}

/**
 * 查询我的项目补充资料
 * GET /my/projects/{id}/supplement-materials
 *
 * @param id 发行项目 ID
 * @returns 项目补充资料附件列表
 */
export function fetchProjectSupplementMaterials(id: number | string) {
  return request<Api.Project.ProjectAttachmentResponse[]>({
    url: `/my/projects/${id}/supplement-materials`,
    method: 'GET'
  });
}

/**
 * 上传我的项目补充资料
 * POST /my/projects/{id}/supplement-materials
 *
 * @param id 发行项目 ID
 * @param data 请求体 (attachments: 附件列表)
 * @returns 更新后的项目补充资料附件列表
 */
export function fetchAddProjectSupplementMaterials(
  id: number | string,
  data: Api.Project.AddProjectSupplementMaterialsRequest
) {
  return request<Api.Project.ProjectAttachmentResponse[]>({
    url: `/my/projects/${id}/supplement-materials`,
    method: 'POST',
    data
  });
}

/**
 * 查询我的发行项目进度时间线
 * GET /my/projects/{id}/timeline
 *
 * @param id 发行项目 ID
 * @param params 查询参数 (types: 事件类型, page: 页码, page_size: 每页数量)
 * @returns 项目进度时间线分页列表
 */
export function fetchProjectTimeline(
  id: number | string,
  params?: Api.Project.QueryTimelineParams
) {
  return request<Api.Project.TimelinePageResponse>({
    url: `/my/projects/${id}/timeline`,
    method: 'GET',
    params
  });
}

/**
 * 获取我的发行项目摘要
 * GET /my/projects/summary
 *
 * @returns 发行项目状态汇总摘要数据
 */
export function fetchProjectsSummary() {
  return request<Api.Project.ProjectSummaryResponse>({
    url: '/my/projects/summary',
    method: 'GET'
  });
}

/**
 * 获取我的合同列表及合同历史
 * GET /my/contracts
 *
 * @param params 查询参数 (page: 页码, page_size: 每页数量)
 * @returns 合同分页列表数据
 */
export function fetchMyContracts(params?: Api.Project.QueryContractsParams) {
  return request<Api.Project.ContractPageResponse>({
    url: '/my/contracts',
    method: 'GET',
    params
  });
}


